import React, { useState, useEffect } from "react";
import {
  X,
  Car,
  Calendar,
  Building2,
  MapPin,
  FileText,
  DollarSign,
  Trash2,
  Lock,
  Unlock,
  Save,
  Edit3,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { api, formatCurrency, formatDate } from "../lib/api";
import { toast } from "sonner";

export default function MileageDetailModal({
  trip,
  properties = [],
  onClose,
  onDelete,
  onUpdate,
  canDelete = false,
  canEdit = true,
  startInEditMode = false,
}) {
  const [currentTrip, setCurrentTrip] = useState(trip);
  const [isLocked, setIsLocked] = useState(!startInEditMode);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    date: trip?.date || "",
    property_id: trip?.property_id || "",
    purpose: trip?.purpose || "",
    miles: trip?.miles != null ? trip.miles.toString() : "",
    irs_rate: trip?.irs_rate != null ? trip.irs_rate.toString() : "",
    notes: trip?.notes || "",
    from_location: trip?.from_location || "",
    to_location: trip?.to_location || "",
  });

  // Sync state if trip prop changes
  useEffect(() => {
    setCurrentTrip(trip);
    setEditForm({
      date: trip?.date || "",
      property_id: trip?.property_id || "",
      purpose: trip?.purpose || "",
      miles: trip?.miles != null ? trip.miles.toString() : "",
      irs_rate: trip?.irs_rate != null ? trip.irs_rate.toString() : "",
      notes: trip?.notes || "",
      from_location: trip?.from_location || "",
      to_location: trip?.to_location || "",
    });
  }, [trip]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("has-modal-open");
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("has-modal-open");
    };
  }, [onClose]);

  const handleCancelEdit = () => {
    setEditForm({
      date: currentTrip.date || "",
      property_id: currentTrip.property_id || "",
      purpose: currentTrip.purpose || "",
      miles: currentTrip.miles != null ? currentTrip.miles.toString() : "",
      irs_rate: currentTrip.irs_rate != null ? currentTrip.irs_rate.toString() : "",
      notes: currentTrip.notes || "",
      from_location: currentTrip.from_location || "",
      to_location: currentTrip.to_location || "",
    });
    setIsLocked(true);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const milesNum = parseFloat(editForm.miles);
    if (isNaN(milesNum) || milesNum <= 0) {
      return toast.error("Please enter a valid number of miles (> 0)");
    }
    if (!editForm.purpose.trim()) {
      return toast.error("Business purpose is required");
    }

    setSaving(true);
    try {
      const payload = {
        date: editForm.date,
        property_id: editForm.property_id || null,
        purpose: editForm.purpose.trim(),
        miles: milesNum,
        irs_rate: editForm.irs_rate ? parseFloat(editForm.irs_rate) : null,
        notes: editForm.notes.trim() || null,
        from_location: editForm.from_location.trim() || null,
        to_location: editForm.to_location.trim() || null,
      };

      const res = await api.patch(`/mileage/${currentTrip.id}`, payload);
      setCurrentTrip(res.data);
      if (onUpdate) onUpdate(res.data);
      setIsLocked(true);
      toast.success("Trip updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update mileage trip");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!window.confirm("Are you sure you want to delete this trip log? This cannot be undone.")) return;
    if (onDelete) onDelete(currentTrip.id);
  };

  // Live deduction estimation for edit mode
  const currentMiles = parseFloat(editForm.miles) || 0;
  const currentRate = parseFloat(editForm.irs_rate) || parseFloat(currentTrip.irs_rate) || 0.70;
  const estimatedDeduction = (currentMiles * currentRate).toFixed(2);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto overscroll-contain scroll-smooth animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="mileage-detail-modal"
    >
      <div
        className="card w-full max-w-xl p-0 overflow-hidden shadow-2xl rounded-t-3xl sm:rounded-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col my-0 sm:my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-[var(--border)] flex items-start justify-between bg-[var(--surface-hover)] min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
            <div className="w-11 h-11 rounded-2xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center shrink-0 shadow-sm">
              <Car size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-lg text-[var(--text)] truncate">
                  {currentTrip.purpose || "Business Trip"}
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 shrink-0">
                  Auto & Travel
                </span>
              </div>
              <div className="text-xs text-[var(--muted)] mt-0.5 truncate">
                Trip ID: <span className="font-mono text-[11px]">{currentTrip.id?.slice(0, 12)}...</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canEdit && (
              <button
                type="button"
                onClick={() => {
                  if (!isLocked) {
                    handleCancelEdit();
                  } else {
                    setIsLocked(false);
                  }
                }}
                className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  isLocked
                    ? "bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
                    : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25"
                }`}
                title={
                  isLocked
                    ? "Protected from accidental changes. Click to unlock & edit."
                    : "Currently unlocked for editing. Click to cancel and lock."
                }
                data-testid="trip-lock-toggle"
              >
                {isLocked ? <Lock size={13} /> : <Unlock size={13} />}
                <span>{isLocked ? "Locked" : "Unlocked"}</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors"
              title="Close"
              data-testid="close-modal-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="p-4 sm:p-6 space-y-5 overflow-y-auto overflow-x-hidden flex-1 min-w-0 overscroll-contain scroll-smooth">
          {/* Edit Mode Warning Banner */}
          {!isLocked && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-between text-xs text-emerald-400 animate-in fade-in duration-150">
              <div className="flex items-center gap-2">
                <Edit3 size={15} className="shrink-0" />
                <span>
                  <strong>Edit Mode Unlocked:</strong> Modify date, miles, property, or purpose below and click Save Changes.
                </span>
              </div>
            </div>
          )}

          {/* VIEW MODE (LOCKED) */}
          {isLocked ? (
            <div className="space-y-5">
              {/* Miles & Deduction Hero */}
              <div className="p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--muted)] font-medium flex items-center gap-1.5">
                    <Car size={13} />
                    <span>Total Miles Driven</span>
                  </div>
                  <div className="text-3xl font-black tabular-nums tracking-tight mt-0.5 text-[var(--text)]">
                    {currentTrip.miles} <span className="text-base font-bold text-[var(--muted)]">mi</span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center justify-end gap-1">
                    <ShieldCheck size={13} />
                    <span>IRS Deduction</span>
                  </div>
                  <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5 tabular-nums">
                    {formatCurrency(currentTrip.total_deduction)}
                  </div>
                  <div className="text-[11px] text-[var(--muted)] font-medium mt-0.5">
                    Rate: ${parseFloat(currentTrip.irs_rate || 0.70).toFixed(2)}/mi
                  </div>
                </div>
              </div>

              {/* Details List */}
              <div className="p-4 rounded-2xl bg-[var(--surface)] border border-[var(--border)] space-y-3.5 text-xs">
                <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
                  <span className="text-[var(--muted)] font-medium flex items-center gap-1.5">
                    <Calendar size={14} /> Date
                  </span>
                  <span className="font-semibold text-[var(--text)] text-sm">
                    {formatDate(currentTrip.date)}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
                  <span className="text-[var(--muted)] font-medium flex items-center gap-1.5">
                    <Building2 size={14} /> Related Property
                  </span>
                  <span className="font-semibold text-[var(--text)] text-right">
                    {currentTrip.property_name || "— General / Portfolio-wide —"}
                  </span>
                </div>

                <div className="flex items-start justify-between pb-3 border-b border-[var(--border)]">
                  <span className="text-[var(--muted)] font-medium flex items-center gap-1.5 shrink-0">
                    <FileText size={14} /> Business Purpose
                  </span>
                  <span className="font-semibold text-[var(--text)] text-right max-w-[70%]">
                    {currentTrip.purpose}
                  </span>
                </div>

                {(currentTrip.from_location || currentTrip.to_location) && (
                  <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
                    <span className="text-[var(--muted)] font-medium flex items-center gap-1.5 shrink-0">
                      <MapPin size={14} /> Route
                    </span>
                    <span className="font-medium text-[var(--text)] text-right">
                      {[currentTrip.from_location, currentTrip.to_location].filter(Boolean).join(" → ")}
                    </span>
                  </div>
                )}

                {currentTrip.notes && (
                  <div className="pt-0.5">
                    <span className="text-[var(--muted)] font-medium block mb-1">Notes / Memo</span>
                    <div className="p-2.5 rounded-xl bg-[var(--surface-hover)] text-[var(--text)] italic text-xs">
                      {currentTrip.notes}
                    </div>
                  </div>
                )}
              </div>

              {/* Schedule E Tax Rules Card */}
              <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-700 dark:text-emerald-400 space-y-1">
                <div className="font-bold flex items-center gap-1.5">
                  <ShieldCheck size={14} />
                  <span>IRS Schedule E Tax Treatment (Line 6 - Auto and Travel)</span>
                </div>
                <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
                  Business driving for property inspections, tenant management, and maintenance supply runs is 100% deductible using the IRS Standard Mileage Rate ($0.70/mi for 2025/2026).
                </p>
              </div>

              {canEdit && (
                <div className="pt-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsLocked(false)}
                    className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 font-semibold"
                    data-testid="edit-trip-btn"
                  >
                    <Edit3 size={14} />
                    <span>Edit Trip</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* EDIT MODE (UNLOCKED) */
            <form onSubmit={handleSave} className="space-y-4" data-testid="edit-trip-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Date */}
                <div>
                  <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="lapen-input text-xs"
                    required
                    data-testid="edit-trip-date"
                  />
                </div>

                {/* Miles */}
                <div>
                  <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                    Miles Driven *
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type="number"
                      step="any"
                      min="0.1"
                      value={editForm.miles}
                      onChange={(e) => setEditForm({ ...editForm, miles: e.target.value })}
                      className="lapen-input text-xs pr-8 font-bold"
                      required
                      placeholder="0.0"
                      data-testid="edit-trip-miles"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none select-none">
                      mi
                    </span>
                  </div>
                </div>
              </div>

              {/* Purpose */}
              <div>
                <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                  Business Purpose *
                </label>
                <input
                  type="text"
                  value={editForm.purpose}
                  onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })}
                  className="lapen-input text-xs"
                  placeholder="e.g. Travel to hardware store for plumbing supplies"
                  required
                  data-testid="edit-trip-purpose"
                />
              </div>

              {/* Property */}
              <div>
                <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                  Related Property (optional)
                </label>
                <select
                  value={editForm.property_id}
                  onChange={(e) => setEditForm({ ...editForm, property_id: e.target.value })}
                  className="lapen-input text-xs"
                  data-testid="edit-trip-property"
                >
                  <option value="">— General / Portfolio-wide —</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.address ? `(${p.address})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Route: From and To */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                    From Location (optional)
                  </label>
                  <input
                    type="text"
                    value={editForm.from_location}
                    onChange={(e) => setEditForm({ ...editForm, from_location: e.target.value })}
                    className="lapen-input text-xs"
                    placeholder="e.g. Home Office"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                    To Location (optional)
                  </label>
                  <input
                    type="text"
                    value={editForm.to_location}
                    onChange={(e) => setEditForm({ ...editForm, to_location: e.target.value })}
                    className="lapen-input text-xs"
                    placeholder="e.g. Rental Property"
                  />
                </div>
              </div>

              {/* Custom IRS Rate */}
              <div>
                <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                  IRS Mileage Rate ($/mi, blank = standard $0.70)
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none select-none z-10">$</span>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={editForm.irs_rate}
                    onChange={(e) => setEditForm({ ...editForm, irs_rate: e.target.value })}
                    className="lapen-input text-xs !pl-8"
                    style={{ paddingLeft: "2rem" }}
                    placeholder="0.70"
                    data-testid="edit-trip-rate"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                  Notes / Memo (optional)
                </label>
                <input
                  type="text"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="lapen-input text-xs"
                  placeholder="Additional context or odometer notes"
                  data-testid="edit-trip-notes"
                />
              </div>

              {/* Live Calculation Box */}
              <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-between text-xs text-indigo-400">
                <span className="font-medium">Estimated IRS Deduction:</span>
                <span className="text-sm font-black tabular-nums">
                  ${estimatedDeduction}
                </span>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2.5 pt-3 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className="btn-secondary text-xs py-2 px-3.5"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
                  data-testid="save-trip-btn"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  <span>{saving ? "Saving…" : "Save Changes"}</span>
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-[var(--border)] flex items-center justify-between bg-[var(--surface-hover)] min-w-0">
          <div>
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-3 py-1.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                title="Delete this mileage trip"
                data-testid="delete-trip-btn"
              >
                <Trash2 size={14} />
                <span>Delete Trip</span>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs py-1.5 px-4 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
